import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { Product } from '@/models/product';
import { CreateInvoiceData } from '@/models/invoice';
import './SmartProductsList.css';

export interface SmartProductsListProps {
  products: CreateInvoiceData['products'];
  allProducts: Product[];
  onRemoveProduct: (index: number) => void;
  onUpdateProduct: (index: number, updates: { quantity?: number; poNumber?: number }) => void;
  onReorderProducts: (fromIndex: number, toIndex: number) => void;
  formatCurrency: (amount: number) => string;
  disabled?: boolean;
  loading?: boolean;
}

const SmartProductsList: React.FC<SmartProductsListProps> = ({
  products,
  allProducts,
  onRemoveProduct,
  onUpdateProduct,
  onReorderProducts,
  formatCurrency,
  disabled = false,
  loading = false
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editField, setEditField] = useState<'quantity' | 'poNumber' | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index.toString());
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  // Handle drag leave
  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      onReorderProducts(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Handle edit start
  const handleEditStart = (index: number, field: 'quantity' | 'poNumber', currentValue: string | number) => {
    setEditingIndex(index);
    setEditField(field);
    setEditValue(currentValue.toString());
  };

  // Handle edit save
  const handleEditSave = (index: number) => {
    if (editField === 'quantity') {
      const quantity = parseInt(editValue);
      if (!isNaN(quantity) && quantity > 0) {
        onUpdateProduct(index, { quantity });
      }
      // Invalid/empty qty — silently cancel, keeps original value
    } else if (editField === 'poNumber') {
      const poNumber = editValue.trim() === '' ? 0 : parseInt(editValue) || 0;
      onUpdateProduct(index, { poNumber });
    }
    setEditingIndex(null);
    setEditField(null);
    setEditValue('');
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditField(null);
    setEditValue('');
  };

  return (
    <Box className="smart-products-list">
      {/* Header */}
      <Box className="spl-products-header">
        <Box className="spl-header-left">
          <Typography variant="h6" className="spl-products-title">
            Products List
          </Typography>
        </Box>
        <Box className="spl-header-actions">
          <Chip
            label={`${products.length} items`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Content */}
      <Box className="spl-products-content">
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
            <CircularProgress />
          </Box>
        ) : products.length > 0 ? (
          <Box className="spl-products-list">
            {products.map((productInfo, index) => {
              console.log(`Looking for product with ID: ${productInfo.product}`);
              const product = allProducts.find(p => p._id === productInfo.product);
              console.log(`Found product:`, product);
              if (!product) {
                console.log(`Product not found for ID: ${productInfo.product}`);
                return null;
              }
              
              const isDragging = draggedIndex === index;
              const isDragOver = dragOverIndex === index;
              const isEditing = editingIndex === index;

              return (
                <Box
                  key={index}
                  className={`spl-product-item ${isDragging ? 'spl-dragging' : ''} ${isDragOver ? 'spl-drag-over' : ''}`}
                  draggable={!disabled && !isEditing}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={() => {
                    setDraggedIndex(null);
                    setDragOverIndex(null);
                  }}
                >
                  <Box className="spl-product-content">
                    <Box className="spl-product-drag-handle">
                      <DragIndicatorIcon className="spl-drag-icon" />
                    </Box>
                    <Box className="spl-product-primary">
                      <Typography variant="body2" className="spl-product-name">
                        {product.item_name}
                      </Typography>
                      <Box className="spl-product-secondary">
                        <Typography variant="caption" className="spl-product-code">
                          {product.item_code}
                        </Typography>
                        {product.hsn_code > 0 && (
                          <Typography variant="caption" className="spl-product-badge">
                            HSN {product.hsn_code}
                          </Typography>
                        )}
                        {product.gst_percentage > 0 && (
                          <Typography variant="caption" className="spl-product-badge">
                            GST {product.gst_percentage}%
                          </Typography>
                        )}
                        {isEditing && editField === 'poNumber' ? (
                          <Box className="spl-edit-field">
                            <TextField
                              size="small"
                              type="number"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleEditSave(index);
                                } else if (e.key === 'Escape') {
                                  handleEditCancel();
                                }
                              }}
                              onBlur={() => handleEditSave(index)}
                              autoFocus
                              placeholder="PO Number"
                              className="spl-inline-edit"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditSave(index)}
                                      className="spl-edit-save-btn"
                                    >
                                      <CheckIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Box>
                        ) : productInfo.poNumber && productInfo.poNumber > 0 ? (
                          <Tooltip title="Click to edit">
                            <Typography
                              variant="caption"
                              className="spl-product-badge po spl-editable"
                              onClick={() => !disabled && handleEditStart(index, 'poNumber', productInfo.poNumber)}
                            >
                              PO {productInfo.poNumber}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Click to add PO">
                            <Typography
                              variant="caption"
                              className="spl-product-badge po spl-editable spl-add-po"
                              onClick={() => !disabled && handleEditStart(index, 'poNumber', '')}
                            >
                              + Add PO
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    <Box className="spl-product-actions">
                      <Box className="spl-product-pricing">
                        <Box className="spl-product-rate-row">
                          <Typography variant="caption" className="spl-product-rate-label">
                            {formatCurrency(product.rate)} ×
                          </Typography>
                          {isEditing && editField === 'quantity' ? (
                            <Box className="spl-edit-field">
                              <TextField
                                size="small"
                                type="number"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleEditSave(index);
                                  } else if (e.key === 'Escape') {
                                    handleEditCancel();
                                  }
                                }}
                                onBlur={() => handleEditSave(index)}
                                autoFocus
                                placeholder="Qty"
                                className="spl-inline-edit"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditSave(index)}
                                        className="spl-edit-save-btn"
                                      >
                                        <CheckIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Box>
                          ) : (
                            <Tooltip title="Click to edit quantity">
                              <Typography
                                variant="caption"
                                className="spl-product-quantity-value spl-editable"
                                onClick={() => !disabled && handleEditStart(index, 'quantity', productInfo.quantity)}
                              >
                                {productInfo.quantity}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="body2" className="spl-product-total">
                          {formatCurrency(product.rate * productInfo.quantity)}
                        </Typography>
                      </Box>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          onClick={() => onRemoveProduct(index)}
                          disabled={disabled || isEditing}
                          className="spl-remove-product-btn"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box className="spl-empty-state">
            <InventoryIcon className="spl-empty-icon" />
            <Typography variant="body2" className="spl-empty-text">
              No products added yet. Use the Smart Product Form above to add products.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SmartProductsList; 